import { getV3Api } from "../apiConfiguration";
import { AppState, Thunk } from "../App";
import { Asset } from "../types/assets";

import { BACK_TO_START_SCREEN, BackToStartScreen } from "./appState";
import { getObjects } from "./simulation";
import {
  Websocket,
  closeWebsocket,
  openBreachWebsocket,
  openPumpDischargeWebsocket,
  openWaterFlowWebsocket,
  openWaterlevelWebsocket,
} from "./websockets";

export type WaterLevelChart = { timestamp: number; value: number }[];

export type WaterFlowChart = {
  timestamp: number;
  velocity: number;
  discharge: number;
}[];

export type PumpDischargeChart = {
  timestamp: number;
  value: number;
}[];

export type BreachChart = {
  timestamp: number;
  discharge: number; // m³/s
  width: number; // m
  depth: number; // m
  area: number; // m²
}[];

interface SelectedAsset {
  id: string | null;
  demValue: number | null;
  waterLevelChart: WaterLevelChart;
  waterFlowChart: WaterFlowChart;
  pumpDischargeChart: PumpDischargeChart;
  breachChart: BreachChart;
}
const INITIAL_STATE: SelectedAsset = {
  id: null,
  waterLevelChart: [],
  waterFlowChart: [],
  pumpDischargeChart: [],
  breachChart: [],
  demValue: null,
};

// Action types
export const SET = "selectedAsset/SET";
const SET_DEM_VALUE = "selectedAsset/SET_DEM_VALUE";
const REMOVE = "selectedAsset/REMOVE";
const ADD_WATERLEVELCHART_DATA = "selectedAsset/ADD_WATERLEVELCHART_DATA";
const ADD_WATERFLOWCHART_DATA = "selectedAsset/ADD_WATERFLOWCHART_DATA";
const ADD_PUMPDISCHARGECHART_DATA = "selectedAsset/ADD_PUMPDISCHARGECHART_DATA";
const ADD_BREACHCHART_DATA = "selectedAsset/ADD_BREACHCHART_DATA";
export const getId = (state: AppState) => state.selectedAsset.id;
export const getSelectedAsset = (state: AppState): Asset | null =>
  state.selectedAsset.id === null ? null : state.simulation.objects[state.selectedAsset.id] || null;
export const getWaterLevelChart = (state: AppState): WaterLevelChart =>
  state.selectedAsset.waterLevelChart;
export const getWaterFlowChart = (state: AppState): WaterFlowChart =>
  state.selectedAsset.waterFlowChart;
export const getPumpDischargeChart = (state: AppState): PumpDischargeChart =>
  state.selectedAsset.pumpDischargeChart;
export const getBreachChart = (state: AppState): BreachChart => state.selectedAsset.breachChart;
export const getDemValue = (state: AppState): number | null => state.selectedAsset.demValue;

export interface Set {
  type: typeof SET;
  id: string;
}
export interface SetDemValue {
  type: typeof SET_DEM_VALUE;
  value: number;
}
interface Remove {
  type: typeof REMOVE;
}
interface AddWaterlevelchartData {
  type: typeof ADD_WATERLEVELCHART_DATA;
  data: WaterLevelChart;
}
interface AddWaterflowchartData {
  type: typeof ADD_WATERFLOWCHART_DATA;
  data: WaterFlowChart;
}
interface AddPumpDischargeChartData {
  type: typeof ADD_PUMPDISCHARGECHART_DATA;
  data: PumpDischargeChart;
}
interface AddBreachChartData {
  type: typeof ADD_BREACHCHART_DATA;
  data: BreachChart;
}
type SelectedAssetAction =
  | Set
  | Remove
  | AddWaterlevelchartData
  | AddWaterflowchartData
  | AddPumpDischargeChartData
  | AddBreachChartData
  | BackToStartScreen
  | SetDemValue;

export const selectAsset =
  (id: string): Thunk =>
  async (dispatch, getState) => {
    dispatch(set(id));

    // Set the new id in Redux state, but also open the correct Websocket for charts
    const state = getState();
    const objects = getObjects(state);

    const asset: Asset = objects[id];

    if (asset.properties.model_type === "pumps") {
      dispatch(openPumpDischargeWebsocket(asset));
    } else if (asset.properties.model_type === "breaches") {
      dispatch(openBreachWebsocket(asset));
    } else if (asset.geometry.type === "Point") {
      // point elements except pumps
      dispatch(openWaterlevelWebsocket(asset, Websocket.WaterlevelChart));
    } else {
      // line elements
      dispatch(openWaterFlowWebsocket(asset));
    }

    // get dem value at point
    if (
      asset.properties.model_type === "nodes"
      // also needed for others?
    ) {
      const v3Api = getV3Api();
      const latLong = {
        lng: asset.properties.coordinates[0],
        lat: asset.properties.coordinates[1],
      };
      const lineEWKT = `LINESTRING( ${latLong.lng} ${latLong.lat} , ${latLong.lng} ${latLong.lat} )`;

      try {
        const simulation = getState().simulation.simulation;
        if (simulation === null) return;
        const profileSimulationId = simulation.id;
        if (profileSimulationId === undefined) return;
        const profiles = await v3Api.simulationsVisualisationsProfileList({
          simulationPk: profileSimulationId,
          layer: "dem",
        });
        const { url } = profiles;
        const response = await fetch(url.replace("{line}", lineEWKT).replace("{samples}", "2"));
        const values: number[] = await response.json();
        dispatch({
          type: SET_DEM_VALUE,
          value: values[0],
        });
      } catch (error) {
        console.error("Caught error wait for dem profile id :", error);
        return;
      }
    }
  };

const set = (id: string): SelectedAssetAction => {
  return {
    type: SET,
    id,
  };
};
export const remove = (): Thunk => (dispatch) => {
  dispatch({
    type: REMOVE,
  });
  // Close current active WaterlevelChart, WaterFlowChart and PumpDischargeChart websockets
  dispatch(closeWebsocket(Websocket.WaterlevelChart));
  dispatch(closeWebsocket(Websocket.WaterFlowChart));
  dispatch(closeWebsocket(Websocket.PumpDischargeChart));
  dispatch(closeWebsocket(Websocket.BreachChart));
};

export const addWaterlevelchartData = (data: WaterLevelChart) => {
  return {
    type: ADD_WATERLEVELCHART_DATA,
    data: data,
  };
};
export const addWaterflowchartData = (data: WaterFlowChart) => {
  return {
    type: ADD_WATERFLOWCHART_DATA,
    data: data,
  };
};
export const addPumpDischargeChartData = (data: PumpDischargeChart) => {
  return {
    type: ADD_PUMPDISCHARGECHART_DATA,
    data: data,
  };
};

export const addBreachChartData = (data: BreachChart) => {
  return {
    type: ADD_BREACHCHART_DATA,
    data: data,
  };
};

export default function reducer(state = INITIAL_STATE, action: SelectedAssetAction) {
  switch (action.type) {
    case BACK_TO_START_SCREEN:
      return INITIAL_STATE;
    case SET: {
      const { id } = action;
      return {
        ...state,
        id,
        waterLevelChart: [],
        waterFlowChart: [],
        pumpDischargeChart: [],
        breachChart: [],
        demValue: null,
      };
    }
    case SET_DEM_VALUE: {
      const { value } = action;
      return {
        ...state,
        demValue: value,
      };
    }
    case REMOVE: {
      return {
        ...state,
        id: null,
        waterLevelChart: [],
        waterFlowChart: [],
        pumpDischargeChart: [],
        breachChart: [],
        demValue: null,
      };
    }
    case ADD_WATERLEVELCHART_DATA: {
      return {
        ...state,
        waterLevelChart: [...state.waterLevelChart, ...action.data],
      };
    }
    case ADD_WATERFLOWCHART_DATA: {
      return {
        ...state,
        waterFlowChart: [...state.waterFlowChart, ...action.data],
      };
    }
    case ADD_PUMPDISCHARGECHART_DATA: {
      return {
        ...state,
        pumpDischargeChart: [...state.pumpDischargeChart, ...action.data],
      };
    }
    case ADD_BREACHCHART_DATA: {
      return {
        ...state,
        breachChart: [...state.breachChart, ...action.data],
      };
    }
    default:
      return state;
  }
}
